/**
 * Vendor Bid Tax Hook
 * 
 * Manages tax calculations for vendor bid submissions using the ZipTax API.
 * Provides functionality to calculate, edit, and store tax estimates for
 * each origin/destination/specification/quantity combination.
 */

import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { usePlatformSetting } from './usePlatformSettings';
import { BidLineItemInput } from './useBidLineItems';

export interface TaxCalculation {
  lineItemKey: string;
  specificationId: string;
  quantityOption: number;
  destinationLocationId: string;
  destinationName: string;
  originZip: string;
  destinationZip: string;
  quantity: number;
  taxableAmountCents: number;
  calculatedTaxCents: number;
  vendorTaxCents: number;
  taxRatePercentage: number;
  isEdited: boolean;
  ziptaxResponse?: any;
}

export interface TaxCalculationResult {
  success: boolean;
  enabled: boolean;
  totalTaxCents: number;
  calculations: TaxCalculation[];
  error?: string;
}

export const useVendorBidTax = (rfqId: string) => {
  const [taxCalculations, setTaxCalculations] = useState<TaxCalculation[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  
  const { data: taxEnabledSetting, isLoading: settingLoading } = usePlatformSetting('vendor_based_tax_enabled');
  const taxEnabled = taxEnabledSetting?.setting_value === 'true';

  const calculateTax = useCallback(async (lineItems: BidLineItemInput[]): Promise<TaxCalculationResult> => {
    if (!taxEnabled) {
      return {
        success: false,
        enabled: false,
        totalTaxCents: 0,
        calculations: [],
        error: 'Vendor-based tax is not enabled',
      };
    }

    if (lineItems.length === 0) {
      return {
        success: false,
        enabled: true,
        totalTaxCents: 0,
        calculations: [],
        error: 'No line items to calculate tax for',
      };
    }

    setIsCalculating(true);
    setError(null);

    try {
      const { data, error: fnError } = await invokeEdgeFunction('calculate-vendor-bid-tax', {
        body: {
          rfqId,
          lineItems: lineItems.map(li => ({
            specificationId: li.specification_id,
            quantityOption: li.quantity_option,
            unitPriceCents: li.unit_price_cents,
            originLocationId: li.origin_location_id,
          })),
        },
        callerOperation: 'useVendorBidTax.calculateTax',
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to calculate tax');
      }

      const taxResult = data as { success: boolean; error?: string; totalTaxCents: number; calculations: unknown[] };

      if (!taxResult.success) {
        throw new Error(taxResult.error || 'Tax calculation failed');
      }

      // Map calculations and set vendorTaxCents to calculated value initially
      const calculations: TaxCalculation[] = (taxResult.calculations || []).map((calc: unknown) => {
        const c = calc as { calculatedTaxCents: number };
        return {
          ...c,
          vendorTaxCents: c.calculatedTaxCents,
          isEdited: false,
        } as TaxCalculation;
      });

      setTaxCalculations(calculations);
      setHasCalculated(true);

      return {
        success: true,
        enabled: true,
        totalTaxCents: taxResult.totalTaxCents,
        calculations,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tax calculation failed';
      setError(message);
      return {
        success: false,
        enabled: true,
        totalTaxCents: 0,
        calculations: [],
        error: message,
      };
    } finally {
      setIsCalculating(false);
    }
  }, [rfqId, taxEnabled]);

  const updateTaxAmount = useCallback((lineItemKey: string, newAmountCents: number) => {
    setTaxCalculations(prev => 
      prev.map(calc => 
        calc.lineItemKey === lineItemKey
          ? { ...calc, vendorTaxCents: newAmountCents, isEdited: true }
          : calc
      )
    );
  }, []);

  const resetTaxAmount = useCallback((lineItemKey: string) => {
    setTaxCalculations(prev =>
      prev.map(calc =>
        calc.lineItemKey === lineItemKey
          ? { ...calc, vendorTaxCents: calc.calculatedTaxCents, isEdited: false }
          : calc
      )
    );
  }, []);

  const resetAllTaxAmounts = useCallback(() => {
    setTaxCalculations(prev =>
      prev.map(calc => ({
        ...calc,
        vendorTaxCents: calc.calculatedTaxCents,
        isEdited: false,
      }))
    );
  }, []);

  const setAllToZero = useCallback(() => {
    setTaxCalculations(prev =>
      prev.map(calc => ({
        ...calc,
        vendorTaxCents: 0,
        isEdited: true,
      }))
    );
  }, []);

  const getTotalVendorTax = useCallback(() => {
    return taxCalculations.reduce((sum, calc) => sum + calc.vendorTaxCents, 0);
  }, [taxCalculations]);

  const getTotalCalculatedTax = useCallback(() => {
    return taxCalculations.reduce((sum, calc) => sum + calc.calculatedTaxCents, 0);
  }, [taxCalculations]);

  const hasEdits = useCallback(() => {
    return taxCalculations.some(calc => calc.isEdited);
  }, [taxCalculations]);

  const clearCalculations = useCallback(() => {
    setTaxCalculations([]);
    setHasCalculated(false);
    setError(null);
  }, []);

  return {
    taxEnabled,
    taxEnabledLoading: settingLoading,
    taxCalculations,
    isCalculating,
    hasCalculated,
    error,
    calculateTax,
    updateTaxAmount,
    resetTaxAmount,
    resetAllTaxAmounts,
    setAllToZero,
    getTotalVendorTax,
    getTotalCalculatedTax,
    hasEdits,
    clearCalculations,
  };
};